import React, { useEffect, useState } from "react";
import { notification } from "antd";
import { FoCard, FoChips, FoDialog, FoKpis, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, TextField, useOsResource } from "./osForm";
import { formatStamp, pageMeta } from "./osRules";

export default function CashAndBank() {
  const [matched, setMatched] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [cash, setCash] = useState("");
  const [bank, setBank] = useState("");
  const [selected, setSelected] = useState([]);
  const [lineOpen, setLineOpen] = useState(false);
  const [line, setLine] = useState({ date: "", description: "", credit: "", debit: "" });
  const [saving, setSaving] = useState(false);
  const view = useOsResource(
    (auth) => os.cashBank(auth, { page, limit, matched: matched === "" ? undefined : matched }),
    [page, limit, matched]
  );
  const pack = pageMeta(view.data?.lines);

  useEffect(() => {
    if (!view.data) return;
    setCash(view.data.cashOnHand == null ? "" : String(view.data.cashOnHand));
    setBank(view.data.bankBalance == null ? "" : String(view.data.bankBalance));
  }, [view.data]);

  const saveBalances = async () => {
    const body = {};
    if (String(view.data?.cashOnHand ?? "") !== cash) body.cashOnHand = Number(cash);
    if (String(view.data?.bankBalance ?? "") !== bank) body.bankBalance = Number(bank);
    if (!Object.keys(body).length) return;
    if (Object.values(body).some((value) => !Number.isFinite(value))) {
      notification.error({ message: "Balances must be numbers." });
      return;
    }
    setSaving(true);
    try {
      await os.saveCashBank(view.token, body);
      notification.success({ message: "Cash position saved." });
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  const addLine = async () => {
    const credit = Number(line.credit) || 0;
    const debit = Number(line.debit) || 0;
    if (!line.date || !line.description || (credit <= 0 && debit <= 0)) {
      notification.error({ message: "Date, description, and a credit or debit are required." });
      return;
    }
    setSaving(true);
    try {
      await os.addCashLine(view.token, { date: line.date, description: line.description, credit, debit });
      notification.success({ message: "Line added." });
      setLineOpen(false);
      setLine({ date: "", description: "", credit: "", debit: "" });
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  const reconcile = async (lineIds) => {
    setSaving(true);
    try {
      await os.reconcile(view.token, lineIds ? { lineIds } : {});
      notification.success({ message: "Lines matched." });
      setSelected([]);
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fo-stack">
      <FoKpis
        items={[
          { label: "Cash on hand", value: `${formatEgp(view.data?.cashOnHand)} EGP` },
          { label: "Bank balance", value: `${formatEgp(view.data?.bankBalance)} EGP` },
          { label: "Unreconciled", value: view.data?.unreconciledCount || 0, hot: (view.data?.unreconciledCount || 0) > 0 },
          { label: "Last reconciled", value: formatStamp(view.data?.lastReconciledAt) },
        ]}
      />
      <FoCard title="Book balances" note="Saving these amounts moves accounts 1001 Cash and 1020 Bank. Zero is a real balance until someone records one.">
        <div className="fo-form-grid">
          <TextField label="Cash on hand" type="number" value={cash} onChange={setCash} />
          <TextField label="Bank balance" type="number" value={bank} onChange={setBank} />
          <button type="button" className="fo-btn fo-btn-primary" disabled={saving} onClick={saveBalances}>Save balances</button>
        </div>
      </FoCard>
      <div className="fo-tools">
        <FoChips
          options={[{ value: "", label: "All lines" }, { value: "false", label: "Unmatched" }, { value: "true", label: "Matched" }]}
          value={matched}
          onChange={(value) => { setPage(1); setMatched(value); }}
        />
        <button type="button" className="fo-btn" disabled={saving} onClick={() => setLineOpen(true)}>Add line</button>
        <button type="button" className="fo-btn" disabled={saving || selected.length === 0} onClick={() => reconcile(selected)}>Match selected</button>
        <button type="button" className="fo-btn fo-btn-primary" disabled={saving} onClick={() => reconcile()}>Match all open</button>
      </div>
      <FoCard title="Bank lines">
        <PagerTable
          loading={view.loading}
          dataSource={pack.docs}
          rowKey="_id"
          page={page}
          limit={limit}
          total={pack.total}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No bank lines yet."
          columns={[
            {
              title: "",
              key: "pick",
              render: (_, row) => row.matched ? null : (
                <input
                  type="checkbox"
                  aria-label={`Select ${row.description}`}
                  checked={selected.includes(row._id)}
                  onChange={(event) => setSelected((current) => event.target.checked ? [...current, row._id] : current.filter((id) => id !== row._id))}
                />
              ),
            },
            { title: "Date", dataIndex: "date" },
            { title: "Description", dataIndex: "description" },
            { title: "Credit", dataIndex: "credit", render: (value) => formatEgp(value) },
            { title: "Debit", dataIndex: "debit", render: (value) => formatEgp(value) },
            { title: "Matched", dataIndex: "matched", render: (value) => statusBadge(value ? "Yes" : "No") },
            { title: "Matched at", dataIndex: "matchedAt", render: (value) => formatStamp(value) },
          ]}
        />
      </FoCard>
      <FoDialog open={lineOpen} title="Add bank line" onClose={() => !saving && setLineOpen(false)}>
        <div className="fo-form-grid">
          <TextField label="Date" type="date" value={line.date} onChange={(date) => setLine({ ...line, date })} />
          <TextField label="Description" value={line.description} onChange={(description) => setLine({ ...line, description })} />
          <TextField label="Credit" type="number" value={line.credit} onChange={(credit) => setLine({ ...line, credit })} />
          <TextField label="Debit" type="number" value={line.debit} onChange={(debit) => setLine({ ...line, debit })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={saving} onClick={addLine}>Save line</button>
        </div>
      </FoDialog>
    </div>
  );
}
